import React, { Dispatch, SetStateAction, useState } from 'react'
import { SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import ControlledMultiFileDropzone from 'transfix-form/components/ControlledMultiFileDropzone'
import AddIcon from 'transfix-icons/build/AddIcon'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { IError } from 'types/FMSGraphqlTypes'
import uuid from 'uuid'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IFileCreate } from 'apps/fleet-planner/types/loads'
import { documentUploadCaption } from 'apps/fleet-planner/utils/fileUtils'
import { VALID_CONTENT_TYPES } from '../constants'
import ProvideInsuranceFormField from './ProvideInsuranceFormField'
import { TProvideInsuranceSchema, IProvideInsuranceContact } from './ProvideInsuranceSchema'
import { useProvideInsuranceForm } from './useprovideInsuranceForm'
import { getErrorMessage } from './utils'

export interface IProvideInsuranceFormProps {
  motorCarrierId: string
  defaultValues?: Partial<TProvideInsuranceSchema>
  onSkip: () => void
  onSubmitSuccess: SubmitHandler<TProvideInsuranceSchema>
  onSubmitError?: SubmitErrorHandler<TProvideInsuranceSchema>
  setAgentContacts?: Dispatch<SetStateAction<IProvideInsuranceContact[]>>
  errorMessages?: Pick<IError, 'message'>[]
}

export const ProvideInsuranceForm = ({
  defaultValues = {},
  onSkip,
  onSubmitSuccess,
  onSubmitError = () => {},
  motorCarrierId,
  setAgentContacts = () => {},
  errorMessages = [],
}: IProvideInsuranceFormProps) => {
  const { trackEvent } = useFMSAnalytics()
  const { control, errors, handleSubmit, reset, setValue, trigger } = useProvideInsuranceForm({
    defaultValues,
  })

  const [files, setFiles] = useState<IFileCreate[]>([])
  const [fileTypeError, setFileTypeError] = useState<string>('')
  const { caption, isFilesSizeLimitExceeded } = documentUploadCaption(files) || {}

  const dropzoneError = getErrorMessage(errors, 'insuranceDocuments')

  const addContactHandler = () => {
    trigger().then(isValid => {
      const {
        email,
        fullName,
        insuranceDocuments,
        motorCarrierId,
        organizationName,
        phoneNumber,
      } = control.getValues()

      const formattedinsuranceDocuments = insuranceDocuments.map((document: any) => {
        return {
          file: document.file,
          fileFileName: document.fileFileName,
        }
      })

      if (isValid) {
        setAgentContacts((prevState: IProvideInsuranceContact[]) => [
          ...prevState,
          {
            id: uuid(),
            organizationName,
            email,
            fullName,
            phoneNumber,
            motorCarrierId,
            insuranceDocuments: formattedinsuranceDocuments,
          },
        ])

        reset({
          email: '',
          fullName: '',
          phoneNumber: '',
          insuranceDocuments: [],
          organizationName: '',
        })
      }
    })
  }

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  const fileChangeHandler = (docs: IFileCreate[]) => {
    if (docs) {
      setFileTypeError('')
      setFiles(docs)
      setValue('insuranceDocuments', docs)
    }
  }

  const dropRejectionHandler = () => {
    trackEvent('Setup Guide', 'USER_UPLOAD_INCOMPATIBLE_INSURANCE_FILE')
    setFileTypeError(
      'File type not supported. Accepted file types are .png, .jpeg, .heic, .heif, and .pdf.'
    )
  }

  return (
    <form onSubmit={submitHandler} data-test='ProvideInsuranceForm'>
      <ControlledHiddenInput
        control={control}
        name='motorCarrierId'
        defaultValue={motorCarrierId}
        value={motorCarrierId}
      />

      <ProvideInsuranceFormField
        name='organizationName'
        control={control}
        label='Insurance Organization Name'
        errors={errors}
        placeholder='Enter Organization Name'
      />

      <ProvideInsuranceFormField
        name='fullName'
        control={control}
        label={`Insurance Agent's Full Name`}
        errors={errors}
        placeholder='Enter first and last name'
      />

      <ProvideInsuranceFormField
        name='email'
        control={control}
        label={`Insurance Agent's Email Address`}
        errors={errors}
        placeholder='you@yourcompany.com'
      />

      <ProvideInsuranceFormField
        name='phoneNumber'
        control={control}
        label={`Insurance Agent's Phone`}
        errors={errors}
        placeholder='(555)-123-4567'
        type='number'
      />

      {dropzoneError && (
        <Box mb={2}>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={dropzoneError} />
        </Box>
      )}

      <Box mb={2}>
        <Typography variant='subtitle1' color='textPrimary'>
          Please upload your certificate(s) of insurance for this provider
        </Typography>
      </Box>
      <ControlledMultiFileDropzone
        name='insuranceDocuments'
        control={control}
        defaultValue={[]}
        MultiDropzoneProps={{
          dropZoneText: 'Upload a file or drop it here',
          acceptedFiles: VALID_CONTENT_TYPES,
          onFileChange: fileChangeHandler,
          onDropRejected: dropRejectionHandler,
        }}
      />
      <Box mt={3} mb={1}>
        <Button startIcon={<AddIcon />} onClick={addContactHandler} color='secondary'>
          Add another insurance contact
        </Button>
      </Box>
      <Box display='flex' flexDirection='column'>
        <Typography
          variant='caption'
          color={isFilesSizeLimitExceeded ? 'error' : 'textSecondary'}
          style={{ marginBottom: 8 }}>
          {caption}
        </Typography>
        {fileTypeError && (
          <Typography variant='caption' color='error' style={{ marginBottom: 8 }}>
            {fileTypeError}
          </Typography>
        )}
        {errorMessages.map(({ message: errorMessage }) => {
          return (
            <Typography variant='caption' color='error' key='error' style={{ marginBottom: 8 }}>
              {errorMessage}
            </Typography>
          )
        })}
      </Box>
      <Box mt={3}>
        <Grid container justifyContent='flex-end'>
          <Button color='tertiary' size='large' data-test='skip-button' onClick={onSkip}>
            Skip and Send Later
          </Button>

          <Button
            type='submit'
            color='primary'
            size='large'
            data-test='submit-button'
            disabled={isFilesSizeLimitExceeded}>
            Submit and Continue
          </Button>
        </Grid>
      </Box>
    </form>
  )
}
